import { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Activity, Mail, Lock, User, Loader2, Eye, EyeOff, ArrowLeft, ArrowRight, CheckCircle } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { OTPInput } from "@/shared/components/ui/otp-input"
import { authService } from "@/shared/services/auth"
import { useToast } from "@/shared/hooks/useToast"

// 区域码选项
const REGION_CODES = [
  { code: "0086", name: "中国", flag: "🇨🇳" },
  { code: "0001", name: "美国", flag: "🇺🇸" },
  { code: "0044", name: "英国", flag: "🇬🇧" },
  { code: "0081", name: "日本", flag: "🇯🇵" },
  { code: "0082", name: "韩国", flag: "🇰🇷" },
  { code: "00852", name: "香港", flag: "🇭🇰" },
  { code: "00853", name: "澳门", flag: "🇲🇴" },
  { code: "00886", name: "台湾", flag: "🇹🇼" },
  { code: "0065", name: "新加坡", flag: "🇸🇬" },
]

/**
 * 注册页面 - 分步表单
 * 步骤1: 邮箱验证
 * 步骤2: 完善信息
 * 步骤3: 注册成功
 */
export default function Register() {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToast()

  // 当前步骤: 1=邮箱验证, 2=完善信息, 3=注册成功
  const [step, setStep] = useState(1)
  
  // 表单数据
  const [formData, setFormData] = useState({
    regionCode: "0086",
    email: "",
    code: "",
    name: "",
    password: "",
    confirmPassword: "",
  })

  // 状态
  const [isLoading, setIsLoading] = useState(false)
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [localError, setLocalError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // 倒计时
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  /**
   * 检查密码强度
   */
  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, message: '', valid: false }
    
    let strength = 0
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
    }

    if (checks.length) strength++
    if (checks.uppercase) strength++
    if (checks.lowercase) strength++
    if (checks.number) strength++

    const missing = []
    if (!checks.length) missing.push('至少8位')
    if (!checks.uppercase) missing.push('建议包含大写字母')
    if (!checks.lowercase) missing.push('建议包含小写字母')
    if (!checks.number) missing.push('建议包含数字')

    return {
      strength,
      message: missing.length > 0 ? `建议：${missing.join('、')}` : '密码强度良好',
      valid: checks.length,
    }
  }

  const passwordStrength = getPasswordStrength(formData.password)

  /**
   * 发送验证码
   */
  const handleSendCode = async () => {
    setLocalError(null)

    // 验证邮箱
    if (!formData.email.trim()) {
      setLocalError("请输入邮箱地址")
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setLocalError("请输入有效的邮箱地址")
      return
    }

    setIsSendingCode(true)
    try {
      const result = await authService.sendCode(formData.email.trim(), formData.regionCode)
      
      if (result.success) {
        showSuccess("验证码已发送到您的邮箱")
        setCountdown(60)
      } else {
        setLocalError(result.error || "发送验证码失败")
        showError(result.error || "发送验证码失败")
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "发送验证码失败"
      setLocalError(errorMessage)
      showError(errorMessage)
    } finally {
      setIsSendingCode(false)
    }
  }

  /**
   * 步骤1: 验证邮箱和验证码，进入下一步
   */
  const handleNextStep = () => {
    setLocalError(null)

    if (!formData.email.trim()) {
      setLocalError("请输入邮箱地址")
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setLocalError("请输入有效的邮箱地址")
      return
    }

    if (!formData.code.trim()) {
      setLocalError("请输入验证码")
      return
    }

    if (formData.code.length !== 6) {
      setLocalError("验证码为6位数字")
      return
    }

    // 进入步骤2
    setStep(2)
  }

  /**
   * 步骤2: 提交注册
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    // 验证
    if (!formData.name.trim()) {
      setLocalError("请输入姓名")
      return
    }

    if (!formData.password.trim()) {
      setLocalError("请输入密码")
      return
    }

    if (formData.password.length < 8) {
      setLocalError("密码长度至少8位")
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setLocalError("两次输入的密码不一致")
      return
    }

    setIsLoading(true)
    try {
      const result = await authService.register({
        region_code: formData.regionCode,
        email: formData.email.trim(),
        code: formData.code.trim(),
        name: formData.name.trim(),
        password: formData.password,
      })

      if (result.success) {
        showSuccess("注册成功")
        setStep(3) // 进入成功页面
      } else {
        setLocalError(result.error || "注册失败")
        showError(result.error || "注册失败")
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "注册失败"
      setLocalError(errorMessage)
      showError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * 步骤指示器
   */
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              s < step
                ? 'bg-primary text-primary-foreground'
                : s === step
                ? 'bg-primary text-primary-foreground'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
            }`}
          >
            {s < step ? <CheckCircle className="h-4 w-4" /> : s}
          </div>
          {s < 3 && (
            <div
              className={`w-12 h-0.5 mx-1 ${
                s < step ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo和标题 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <Activity className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">创建账户</h1>
          <p className="text-slate-500 dark:text-slate-400">
            开始使用 Axiom 管理您的 AI Agent 项目
          </p>
        </div>

        {/* 注册卡片 */}
        <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/80 dark:bg-slate-900/80">
          <CardHeader className="space-y-1 pb-4">
            <StepIndicator />
            <CardTitle className="text-2xl font-semibold text-center">
              {step === 1 && "验证邮箱"}
              {step === 2 && "完善信息"}
              {step === 3 && "注册成功"}
            </CardTitle>
            <CardDescription className="text-center">
              {step === 1 && "输入邮箱并验证"}
              {step === 2 && "设置您的账户信息"}
              {step === 3 && "请等待管理员审核"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* 步骤1: 邮箱验证 */}
            {step === 1 && (
              <div className="space-y-5">
                {/* 区域码 + 邮箱输入 */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    邮箱地址
                  </Label>
                  <div className="flex gap-2">
                    {/* 区域码选择 */}
                    <Select
                      value={formData.regionCode}
                      onValueChange={(value) => setFormData({ ...formData, regionCode: value })}
                      disabled={isSendingCode}
                    >
                      <SelectTrigger className="w-[130px] h-11 rounded-lg border-slate-200 dark:border-slate-800">
                        <SelectValue>
                          {REGION_CODES.find(r => r.code === formData.regionCode)?.flag}{" "}
                          {formData.regionCode}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {REGION_CODES.map((region) => (
                          <SelectItem key={region.code} value={region.code}>
                            <span className="flex items-center gap-2">
                              <span>{region.flag}</span>
                              <span>{region.code}</span>
                              <span className="text-slate-500 text-xs">({region.name})</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* 邮箱输入 */}
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="pl-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                        disabled={isSendingCode}
                        autoComplete="email"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>

                {/* 验证码输入 */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">
                      验证码
                    </Label>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      onClick={handleSendCode}
                      disabled={isSendingCode || countdown > 0}
                      className="h-auto p-0 text-primary"
                    >
                      {isSendingCode ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          发送中
                        </>
                      ) : countdown > 0 ? (
                        `${countdown}s 后重发`
                      ) : (
                        "发送验证码"
                      )}
                    </Button>
                  </div>
                  <OTPInput
                    length={6}
                    value={formData.code}
                    onChange={(value) => setFormData({ ...formData, code: value })}
                    disabled={isSendingCode}
                  />
                  <p className="text-xs text-center text-slate-500 dark:text-slate-400">
                    请输入发送到 {formData.email || '您邮箱'} 的6位验证码
                  </p>
                </div>

                {/* 错误提示 */}
                {localError && (
                  <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
                    <p className="text-sm text-destructive">{localError}</p>
                  </div>
                )}

                {/* 下一步按钮 */}
                <Button
                  type="button"
                  onClick={handleNextStep}
                  className="w-full h-11 rounded-lg text-base font-medium shadow-sm hover:shadow-md transition-all"
                >
                  下一步
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* 步骤2: 完善信息 */}
            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* 姓名输入 */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    姓名
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="您的姓名"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="pl-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                      disabled={isLoading}
                      autoComplete="name"
                      autoFocus
                    />
                  </div>
                </div>

                {/* 密码输入 */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    密码
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="至少8位字符"
                      value={formData.password}
                      onChange={(e) => {
                        setFormData({ ...formData, password: e.target.value })
                        setLocalError(null)
                      }}
                      className="pl-10 pr-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                      disabled={isLoading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {/* 密码强度提示 */}
                  {formData.password.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              passwordStrength.strength === 4
                                ? 'bg-green-500'
                                : passwordStrength.strength >= 2
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                            }`}
                            style={{ width: `${(passwordStrength.strength / 4) * 100}%` }}
                          />
                        </div>
                        <span
                          className={`text-xs ${
                            passwordStrength.valid
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-slate-500 dark:text-slate-400'
                          }`}
                        >
                          {passwordStrength.strength}/4
                        </span>
                      </div>
                      <p
                        className={`text-xs ${
                          passwordStrength.valid
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-slate-500 dark:text-slate-400'
                        }`}
                      >
                        {passwordStrength.message}
                      </p>
                    </div>
                  )}
                </div>

                {/* 确认密码输入 */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">
                    确认密码
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="再次输入密码"
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        setFormData({ ...formData, confirmPassword: e.target.value })
                      }
                      className="pl-10 pr-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                      disabled={isLoading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* 错误提示 */}
                {localError && (
                  <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
                    <p className="text-sm text-destructive">{localError}</p>
                  </div>
                )}

                {/* 按钮组 */}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-11 rounded-lg"
                    disabled={isLoading}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    上一步
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-11 rounded-lg text-base font-medium shadow-sm hover:shadow-md transition-all"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        注册中...
                      </>
                    ) : (
                      "创建账户"
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* 步骤3: 注册成功 */}
            {step === 3 && (
              <div className="text-center py-6 space-y-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full">
                  <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">注册成功！</h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    您的账号正在等待管理员审核
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-500">
                    审核通过后您将收到邮件通知
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/login')}
                  className="h-11 px-8 rounded-lg text-base font-medium shadow-sm hover:shadow-md transition-all"
                >
                  返回登录
                </Button>
              </div>
            )}

            {/* 登录链接 */}
            {step !== 3 && (
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  已有账户？{" "}
                  <Link
                    to="/login"
                    className="text-primary font-medium hover:underline"
                  >
                    立即登录
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 底部说明 */}
        <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
          注册即表示您同意我们的服务条款和隐私政策
        </p>
      </div>
    </div>
  )
}
